import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import useAccess from "../../hooks/useAccess";
import "react-quill/dist/quill.snow.css";
import "../../styles/Form.css";

const emptyForm = {
  t_id: null,
  t_title: "",
  t_display_title: "",
  t_content: "",
  t_category_id: "",
  t_requires_ref: 0,
  t_order: 0,
  t_active: 1,
  t_default: 0,

  // ✅ upload toggle
  t_allow_upload: 0,
  t_show_expiry: 0,
  t_show_employee: 1,
  t_show_date: 1,
};

const CustomTaskCreateHelper = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [taskPdf, setTaskPdf] = useState(null);

  // 🧪 Access control
  const { hasPermission } = useAccess();

  const PERMISSIONS = {
    customTask: {
      create: hasPermission("AccountSetting", "Custom Task", "create"),
      edit: hasPermission("AccountSetting", "Custom Task", "edit"),
    },
  };

  const isEditMode = !!form.t_id;

  // --------------------------------------------------
  // Load categories + tasks
  // --------------------------------------------------
  const loadTasks = useCallback(() => {
    axios
      .get(`${API_BASE}/api/custom-tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setTasks(res.data))
      .catch(() => alert("Failed to load custom tasks"));
  }, [API_BASE, token]);

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/custom-tasks/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setCategories(res.data))
      .catch(() => alert("Failed to load categories"));

    loadTasks();
  }, [API_BASE, token, loadTasks]);

  // --------------------------------------------------
  // Change handlers
  // --------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // --------------------------------------------------
  // Create / Save
  // --------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value);
      });

      if (taskPdf) {
        formData.append("t_pdf", taskPdf);
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      if (isEditMode) {
        await axios.put(
          `${API_BASE}/api/custom-tasks/${form.t_id}`,
          formData,
          config,
        );
        alert("✅ Custom Task updated");
      } else {
        await axios.post(`${API_BASE}/api/custom-tasks`, formData, config);
        alert("✅ Custom Task created");
      }

      setForm(emptyForm);
      setTaskPdf(null);
      loadTasks();
    } catch (err) {
      console.error(err);
      alert("❌ Save failed");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // Edit
  // --------------------------------------------------
  const handleEdit = (task) => {
    setForm({
      t_id: task.t_id,
      t_title: task.t_title,
      t_display_title: task.t_display_title,
      t_content: task.t_content || "",
      t_category_id: task.t_category_id,
      t_requires_ref: task.t_requires_ref,
      t_order: task.t_order,
      t_active: task.t_active,
      t_default: task.t_default,
      t_allow_upload: task.t_allow_upload ?? 0,
      t_show_expiry: task.t_show_expiry ?? 0,
      t_show_employee: task.t_show_employee ?? 1,
      t_show_date: task.t_show_date ?? 1,
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="form-wrapper my-2">
      <h2>{isEditMode ? "Edit Custom Task" : "Create Custom Task"}</h2>

      {/* ================= FORM ================= */}
      <form onSubmit={handleSubmit}>
        <label className="form-label mt-3">Admin Title</label>
        <input
          className="form-control mb-3"
          name="t_title"
          value={form.t_title}
          onChange={handleChange}
          required
        />

        <label className="form-label">Display Title</label>
        <input
          className="form-control mb-3"
          name="t_display_title"
          value={form.t_display_title}
          onChange={handleChange}
          required
        />

        <label className="form-label">Category</label>
        <select
          className="form-control mb-3"
          name="t_category_id"
          value={form.t_category_id}
          onChange={handleChange}
          required
        >
          <option value="">-- Select Category --</option>
          {categories.map((c) => (
            <option key={c.tc_id} value={c.tc_id}>
              {c.tc_name}
            </option>
          ))}
        </select>

        <div className="row mt-3">
          <div className="col">
            <label>Requires Ref</label>
            <select
              className="form-control"
              name="t_requires_ref"
              value={form.t_requires_ref}
              onChange={handleChange}
            >
              <option value={0}>No</option>
              <option value={1}>Yes</option>
            </select>
          </div>

          <div className="col">
            <label>Order</label>
            <input
              type="number"
              className="form-control"
              name="t_order"
              value={form.t_order}
              onChange={handleChange}
              min="0"
            />
          </div>

          <div className="col">
            <label>Active</label>
            <select
              className="form-control"
              name="t_active"
              value={form.t_active}
              onChange={handleChange}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col">
            <label>Allow Upload</label>
            <select
              className="form-control"
              name="t_allow_upload"
              value={form.t_allow_upload}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>
          <div className="col">
            <label>Show Expiry Date</label>
            <select
              className="form-control"
              name="t_show_expiry"
              value={form.t_show_expiry}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col">
            <label>Show Employee Name</label>
            <select
              className="form-control"
              name="t_show_employee"
              value={form.t_show_employee}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>

          <div className="col">
            <label>Show Date</label>
            <select
              className="form-control"
              name="t_show_date"
              value={form.t_show_date}
              onChange={handleChange}
            >
              <option value={0}>Off</option>
              <option value={1}>On</option>
            </select>
          </div>
        </div>

        <label className="form-label mt-3">Task PDF (optional)</label>
        <input
          type="file"
          accept="application/pdf"
          className="form-control mb-2"
          onChange={(e) => setTaskPdf(e.target.files[0])}
        />

        {taskPdf && (
          <small className="text-muted">
            PDF uploaded — task content will be ignored
          </small>
        )}

        <label className="form-label mt-3">Task Content</label>
        <ReactQuill
          theme="snow"
          value={form.t_content}
          onChange={(value) =>
            setForm((prev) => ({ ...prev, t_content: value }))
          }
        />

        <button
          className="btn btn-primary mt-4"
          disabled={loading || !PERMISSIONS.customTask.create}
        >
          {loading ? "Saving..." : isEditMode ? "Save" : "Create"}
        </button>

        {isEditMode && (
          <button
            type="button"
            className="btn btn-secondary mt-4 ms-2"
            onClick={() => setForm(emptyForm)}
          >
            Cancel Edit
          </button>
        )}
      </form>

      {/* ================= TABLE ================= */}
      <hr className="my-5" />

      <h4>Customized Tasks</h4>

      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Title</th>
            <th>Key</th>
            <th>Route</th>
            <th>Active</th>
            <th width="100">Action</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => (
            <tr key={t.t_id}>
              <td>{t.t_display_title}</td>
              <td>{t.t_key}</td>
              <td>{t.t_route}</td>
              <td>{t.t_active ? "Yes" : "No"}</td>
              <td>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleEdit(t)}
                  disabled={loading || !PERMISSIONS.customTask.edit}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}

          {!tasks.length && (
            <tr>
              <td colSpan="5" className="text-center">
                No custom tasks found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CustomTaskCreateHelper;
